/* Keyframe evaluation for the hero scene. Every UI animation in the hero is a
   pure function of the loop time t (ms), so a seek (?t=), a pause, the scene
   lab and the reduced-motion posters all show the exact same frame. Only
   transform, opacity and (for the strings) stroke-dashoffset are written.

   A track is a list of keyframes [t, props, ease]. Props: x, y (px), s
   (uniform scale), sx, sy, r (deg), o (opacity), d (string draw 0..1).
   A prop missing from a keyframe carries over from the one before. `ease`
   names the curve INTO that keyframe; it may be a string or an object per
   prop ({ _: default, y: 'inQuad' }). */

function bezier(x1, y1, x2, y2) {
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  const sx = (t) => ((ax * t + bx) * t + cx) * t;
  const sy = (t) => ((ay * t + by) * t + cy) * t;
  const dx = (t) => (3 * ax * t + 2 * bx) * t + cx;
  return (x) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 6; i += 1) {
      const e = sx(t) - x;
      const d = dx(t);
      if (Math.abs(e) < 1e-5 || Math.abs(d) < 1e-6) break;
      t -= e / d;
    }
    return sy(Math.min(1, Math.max(0, t)));
  };
}

function piecewise(points) {
  return (p) => {
    if (p <= 0) return 0;
    if (p >= 1) return 1;
    for (let i = 1; i < points.length; i += 1) {
      const [p1, v1] = points[i];
      if (p <= p1) {
        const [p0, v0] = points[i - 1];
        return v0 + ((v1 - v0) * (p - p0)) / (p1 - p0);
      }
    }
    return 1;
  };
}

/* Motion tokens (motion spec 2.5). */
export const EASE = {
  linear: (p) => p,
  out: bezier(0.2, 0.8, 0.2, 1),
  in: bezier(0.4, 0, 1, 1),
  inOut: bezier(0.45, 0, 0.2, 1),
  outQuad: (p) => 1 - (1 - p) * (1 - p),
  smooth: (p) => p * p * (3 - 2 * p),
  inQuad: (p) => p * p,
  step: (p) => (p < 1 ? 0 : 1),
  springSoft: piecewise([[0, 0], [0.05, 0.18], [0.12, 0.52], [0.2, 0.84], [0.28, 1.02], [0.34, 1.06], [0.4, 1.05], [0.5, 1.01], [0.6, 0.99], [0.75, 1], [1, 1]]),
  springPop: piecewise([[0, 0], [0.06, 0.3], [0.14, 0.8], [0.22, 1.1], [0.27, 1.13], [0.35, 1.08], [0.46, 0.99], [0.54, 0.98], [0.68, 1.01], [1, 1]]),
};

export const DUR = { soft: 520, pop: 280, exit: 260, fade: 200 };

const PROPS = ['x', 'y', 's', 'sx', 'sy', 'r', 'o', 'd'];
const DEFAULTS = { x: 0, y: 0, s: 1, sx: 1, sy: 1, r: 0, o: 1, d: 0 };

/* Resolve a keyframe list: sort, carry props forward, pre-pick ease fns. */
export function keys(list, init = {}) {
  const sorted = list.filter(Boolean).slice().sort((a, b) => a[0] - b[0]);
  let cur = { ...DEFAULTS, ...init };
  return sorted.map(([t, props, ease]) => {
    cur = { ...cur, ...props };
    const e = ease || 'out';
    const pick = (p) => EASE[(typeof e === 'string' ? e : e[p] || e._) || 'out'] || EASE.out;
    return { t, v: cur, ease: Object.fromEntries(PROPS.map((p) => [p, pick(p)])) };
  });
}

/* Value of every prop at time t. Before the first key: the first key. */
export function evalKeys(ks, t) {
  if (!ks.length) return DEFAULTS;
  if (t <= ks[0].t) return ks[0].v;
  const last = ks[ks.length - 1];
  if (t >= last.t) return last.v;
  let i = 1;
  while (i < ks.length && ks[i].t <= t) i += 1;
  const a = ks[i - 1];
  const b = ks[i];
  const span = b.t - a.t;
  const p = span > 0 ? (t - a.t) / span : 1;
  const out = {};
  for (let j = 0; j < PROPS.length; j += 1) {
    const k = PROPS[j];
    const va = a.v[k];
    const vb = b.v[k];
    out[k] = va === vb ? va : va + (vb - va) * b.ease[k](p);
  }
  return out;
}

const r2 = (n) => Math.round(n * 100) / 100;

export function toTransform(v) {
  const parts = [];
  if (v.x || v.y) parts.push(`translate3d(${r2(v.x)}px,${r2(v.y)}px,0)`);
  if (v.r) parts.push(`rotate(${r2(v.r)}deg)`);
  const sx = v.s * v.sx;
  const sy = v.s * v.sy;
  if (sx !== 1 || sy !== 1) parts.push(`scale(${r2(sx * 1000) / 1000},${r2(sy * 1000) / 1000})`);
  return parts.length ? parts.join(' ') : 'none';
}

export const clamp01 = (n) => Math.min(1, Math.max(0, n));
